import { forwardRef, ReactNode, useImperativeHandle, useState } from 'react'
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
} from '@black-bear-energy/rucksack'
import { ResetButton } from './ResetButton'

export type MenuAlign = 'left' | 'right'

export interface MenuButtonHandle {
  close: () => void
}

export const MenuButton = forwardRef<MenuButtonHandle, MenuButtonProps>(
  (
    {
      icon,
      children,
      label,
      reset,
      menuSx,
      keepOpen = true,
      align = 'left',
      onOpen,
      onClose,
    },
    ref
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const open = Boolean(anchorEl)

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget)
      onOpen?.()
    }
    const handleClose = () => {
      setAnchorEl(null)
      onClose?.()
    }

    useImperativeHandle(ref, () => ({ close: handleClose }))

    return (
      <Box sx={{ flexShrink: 0 }}>
        {label ? (
          <Button
            variant="outlined"
            size="large"
            startIcon={icon}
            onClick={handleClick}
          >
            {label}
          </Button>
        ) : (
          <IconButton sx={{ padding: '0px' }} onClick={handleClick}>
            {icon}
          </IconButton>
        )}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: align }}
          transformOrigin={{ vertical: -4, horizontal: align }}
          sx={{ '& .MuiList-root': { py: 0 } }}
        >
          <Box
            sx={{ paddingY: 1, ...menuSx }}
            onMouseUp={keepOpen ? undefined : handleClose}
          >
            {children}
          </Box>
          {reset && (
            <Box component="li">
              <Divider />
              <Box
                sx={{
                  px: 2,
                  py: 0.5,
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
              >
                <ResetButton
                  onClick={reset.handleReset}
                  disabled={reset.resetDisabled}
                />
              </Box>
            </Box>
          )}
        </Menu>
      </Box>
    )
  }
)

MenuButton.displayName = 'MenuButton'

interface MenuButtonProps {
  icon: React.ReactNode
  children: ReactNode
  label?: string
  reset?: { handleReset: () => void; resetDisabled: boolean }
  menuSx?: object
  keepOpen?: boolean
  align?: MenuAlign
  onOpen?: () => void
  onClose?: () => void
}
